import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Home from '../pages/Home';
import './MainContainer.css';
import { appConfig } from '../config/defaultData';
import { 
  fetchNavWebsites, 
  getLastSelectedNav, 
  setLastSelectedNav 
} from '../redux/slices/websiteSlice';
import logger from '../utils/logger';

// 添加一个工具函数来检测弹窗状态
const isPopupVisible = () => {
  // 检查是否有弹窗元素存在并且可见
  // 通过查找所有常见的弹窗覆盖层类名
  const popupSelectors = [
    '.popup-overlay',     // 常规弹窗覆盖层
    '.app-popup-overlay', // 应用添加/编辑弹窗覆盖层
    '.simple-popup-overlay', // 简单弹窗覆盖层
    '.wallpaper-popup',    // 壁纸选择弹窗
    '.settings-drawer-overlay.visible'  // 设置抽屉覆盖层
  ];
  
  // 检查所有可能的弹窗选择器
  for (const selector of popupSelectors) {
    const elements = document.querySelectorAll(selector);
    if (elements.length > 0) {
      return true;
    }
  }
  
  // 检查modal-root元素内是否有内容（React Portal常用容器）
  const modalRoot = document.getElementById('modal-root');
  if (modalRoot && modalRoot.children.length > 0) {
    return true;
  }
  
  return false;
};

// 添加函数检测触摸事件是否发生在可滚动内容上
const isTouchOnScrollableContent = (e) => {
  // **优化**: 更精确的拖拽检测
  const touchTarget = e.touches ? e.touches[0] : e;
  const element = document.elementFromPoint(touchTarget.clientX, touchTarget.clientY);
  
  // **重要**: 检查是否正在进行 @dnd-kit 拖拽
  if (document.body.hasAttribute('data-dragging')) {
    return {
      isScrollable: false,
      element: null
    };
  }
  
  // 检查是否是拖拽相关的元素
  if (element && (
    element.closest('.app-item') ||
    element.closest('[data-dnd-kit-draggable-id]') ||
    element.closest('[draggable="true"]') ||
    element.classList.contains('drag-handle') ||
    element.classList.contains('app-icon-wrapper') ||
    // 添加更多 @dnd-kit 相关的选择器
    element.closest('[data-rbd-draggable-id]') ||
    element.closest('[data-rbd-drag-handle-draggable-id]')
  )) {
    return {
      isScrollable: false,
      element: null
    };
  }
  
  // 获取触摸点所在的元素
  const touch = e.touches[0];
  const touchTargetElement = document.elementFromPoint(touch.clientX, touch.clientY);
  
  // 检查是否在apps-wrapper或其他可滚动容器内
  let currentElement = touchTargetElement;
  while (currentElement) {
    // 检查元素是否为可滚动容器
    if (
      currentElement.classList && 
      (currentElement.classList.contains('apps-wrapper') || 
       currentElement.classList.contains('app-popup-content') ||
       currentElement.getAttribute('data-scrollable') === 'true')
    ) {
      // **优化**: 更严格的拖拽元素检查
      const hasAppItems = currentElement.querySelector('.app-item');
      if (hasAppItems && element && element.closest('.app-item')) {
        // 如果点击在应用图标上，优先考虑拖拽而不是滚动
        return {
          isScrollable: false,
          element: null
        };
      }
      
      // **新增**: 检查是否有任何 @dnd-kit 拖拽正在进行
      if (window._dndKitDragging || document.querySelector('.dnd-kit-dragging')) {
        return {
          isScrollable: false,
          element: null
        };
      }
      
      return {
        isScrollable: true,
        element: currentElement
      };
    }
    currentElement = currentElement.parentElement;
  }
  
  return {
    isScrollable: false,
    element: null
  };
};

// 检查是否是垂直滚动的容器
const isVerticalScrollable = (element) => {
  if (!element) return false;
  
  // 检查元素是否可以垂直滚动
  return element.scrollHeight > element.clientHeight;
};

const MainContainer = forwardRef(({ onCategoryChange, currentCategory }, ref) => {
  // 使用 Redux 钩子获取状态
  const dispatch = useDispatch();
  const { navItems, loading } = useSelector(state => state.nav);
  const { lastSelectedNav } = useSelector(state => state.website);
  
  // 使用状态来控制容器样式而不是ref，确保渲染时能使用最新值
  const [containerStyle, setContainerStyle] = useState({
    height: '100vh',
    transform: 'translateY(0px)',
    transition: 'none',
    willChange: 'transform'
  });
  
  const [initialRenderComplete] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [navPages, setNavPages] = useState([]);
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const scrolling = useRef(false);
  const lastScrollTime = useRef(0);
  // 将 pageHeight 改为状态变量，支持动态更新
  const [pageHeight, setPageHeight] = useState(window.innerHeight);
  const touchMoveY = useRef(0);
  const velocityY = useRef(0);
  const lastTouchY = useRef(0);
  const lastTouchTime = useRef(0);
  const isCircularScrolling = useRef(false);
  const initialTouchY = useRef(0);
  const touchTargetRef = useRef(null);
  const isScrollingContent = useRef(false);
  const lastScrollTop = useRef(0);
  const scrollDirection = useRef(null);
  const pageChangeTime = useRef(Date.now());
  const PAGE_CHANGE_COOLDOWN = 600;
  const canChangePage = useRef(true);
  const isInitialLoad = useRef(true);
  const skipAnimation = useRef(false);
  const initialPositionSet = useRef(false);
  const touchInteractionActive = useRef(false); // 触摸交互是否活跃
  const hasTriggeredTouchPageChange = useRef(false); // 本次触摸是否已经触发过页面切换
  

  // 平滑滚动到指定位置
  const smoothScrollTo = (targetIndex, duration = 200) => {
    // 记录页面切换时间点
    pageChangeTime.current = Date.now();
    // 进入冷却状态
    canChangePage.current = false;
    scrolling.current = true;
    
    // 清除当前页面的所有边界标记
    const currentPage = containerRef.current?.children[activeIndex];
    if (currentPage) {
      const appsWrapper = currentPage.querySelector('.apps-wrapper');
      if (appsWrapper) {
        appsWrapper.removeAttribute('data-at-bottom');
        appsWrapper.removeAttribute('data-at-top');
        appsWrapper.removeAttribute('data-touch-at-bottom');
        appsWrapper.removeAttribute('data-touch-at-top');
      }
    }
    
    // 获取起始位置和目标位置
    const startY = containerRef.current ? parseFloat(getComputedStyle(containerRef.current).transform.split(',')[5]) || 0 : 0;
    const targetY = -targetIndex * pageHeight;
    const startTime = performance.now();
    const easeOutCubic = t => 1 - Math.pow(1 - t, 3); // 使用更平滑的缓动函数
    
    // 执行动画
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      if (containerRef.current) {
        const currentY = startY + (targetY - startY) * easeOutCubic(progress);
        containerRef.current.style.transform = `translateY(${currentY}px)`;
      }
      
      if (progress < 1) {
        // 动画未完成，继续执行
        requestAnimationFrame(animate);
      } else {
        // 动画完成，设置最终位置并更新状态
        if (containerRef.current) {
          containerRef.current.style.transform = `translateY(${targetY}px)`;
        }
        
        // 更新活跃索引
        setActiveIndex(targetIndex);
        
        // 重置滚动状态
        setTimeout(() => {
          scrolling.current = false;
          
          // 冷却期结束后才允许再次切换页面
          setTimeout(() => {
            canChangePage.current = true;
          }, PAGE_CHANGE_COOLDOWN - 300); // 适当调整冷却时间
        }, 50);
      }
    };
    
    // 启动动画
    requestAnimationFrame(animate);
  };

  // 组件挂载时加载最后选择的分类
  useEffect(() => {
    // 检查是否有刷新标记
    try {
      const isRefresh = sessionStorage.getItem('is_page_refresh') === 'true';
      if (isRefresh) {
        sessionStorage.removeItem('is_page_refresh');
        skipAnimation.current = true;
      }
    } catch (error) {
      logger.error('[MainContainer] 检查刷新标记失败:', error);
    }
    
    // 先从 Redux 获取 lastSelectedNav
    dispatch(getLastSelectedNav());
    
    // 添加一个事件，在刷新前保存分类状态
    const handleBeforeUnload = () => {
      const currentNav = lastSelectedNav;
      if (currentNav) {
        try {
          // 保存当前分类到 sessionStorage
          sessionStorage.setItem('temp_lastSelectedNav', currentNav);
          sessionStorage.setItem('is_page_refresh', 'true');
        } catch (error) {
          logger.error('[MainContainer] 保存临时分类失败:', error);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dispatch, lastSelectedNav]);

  // 设置初始位置 - 当导航项和最后选择的分类都准备好后
  useEffect(() => {
    // 如果已经设置过初始位置，跳过
    if (initialPositionSet.current) return;
    
    // 如果导航项还没加载完或者分类信息不完整，跳过
    if (loading || navItems.length === 0) return;
    
    // 更新页面列表
    const initialPages = navItems.map(item => ({
      id: item.id,
      component: <Home category={item.id === 'home' ? undefined : item.id} />
    }));
    setNavPages(initialPages);
    
    // 确定要显示的初始索引
    let initialIndex = 0; // 默认显示首页
    
    // 如果配置允许记住上次选择
    if (appConfig.rememberLastSelectedMenu) {
      // 尝试使用 Redux 中的分类
      let categoryToUse = lastSelectedNav;
      
      // 如果 Redux 中没有有效值，尝试从 sessionStorage 获取
      try {
        const tempCategory = sessionStorage.getItem('temp_lastSelectedNav');
        if (tempCategory && !categoryToUse) {
          categoryToUse = tempCategory;
          // 清除临时存储
          sessionStorage.removeItem('temp_lastSelectedNav');
          
          // 更新 Redux 状态
          dispatch(setLastSelectedNav(categoryToUse));
        }
      } catch (error) {
        logger.error('[MainContainer] 获取临时分类失败:', error);
      }
      
      // 查找对应的导航项索引
      if (categoryToUse) {
        const foundIndex = navItems.findIndex(item => item.id === categoryToUse);
        if (foundIndex !== -1) {
          initialIndex = foundIndex;
          
          // 如果更新了当前分类，通知父组件
          if (onCategoryChange && categoryToUse !== currentCategory) {
            onCategoryChange(categoryToUse);
          }
        }
      }
    }
    
    // 设置初始位置（无动画）
    setActiveIndex(initialIndex);
    
    // 无动画直接跳转到对应位置
    setContainerStyle({
      height: `${initialPages.length * 100}vh`,
      transform: `translateY(-${initialIndex * pageHeight}px)`,
      transition: 'none',
      willChange: 'transform'
    });
    
    // 短暂延迟后启用过渡动画
    setTimeout(() => {
      setContainerStyle(prev => ({
        ...prev,
        transition: 'transform 0.3s cubic-bezier(0.33, 1, 0.68, 1)'
      }));
      
      // 标记初始位置已设置
      initialPositionSet.current = true;
      isInitialLoad.current = false;
      
      // 启用页面切换
      canChangePage.current = true;
    }, 100);
  }, [loading, navItems, lastSelectedNav, pageHeight, onCategoryChange, currentCategory, dispatch]);

  // 监听activeIndex变化，更新容器位置（仅在初始加载时和特殊情况下）
  useEffect(() => {
    // 如果是初始加载或者没有页面，跳过
    if (navPages.length === 0) {
      return;
    }
    
    // 如果正在拖拽或动画冻结期，不更新位置
    const isDragging = document.body.hasAttribute('data-dragging');
    const isAnimationFrozen = document.body.classList.contains('animation-freeze');
    if (isDragging || isAnimationFrozen) {
      return;
    }
    
    // 通知父组件当前分类变化
    if (activeIndex >= 0 && activeIndex < navPages.length) {
      onCategoryChange && onCategoryChange(navPages[activeIndex].id);
      
      // 更新 Redux 中的最后选择的分类
      if (appConfig.rememberLastSelectedMenu) {
        dispatch(setLastSelectedNav(navPages[activeIndex].id));
      }
      
      // 仅在初始加载或特定情况下直接设置位置（无动画）
      if (isInitialLoad.current || skipAnimation.current || window.skipMainContainerAnimation) {
        // 直接设置位置，无动画
        if (containerRef.current) {
          containerRef.current.style.transition = 'none';
          containerRef.current.style.transform = `translateY(-${activeIndex * pageHeight}px)`;
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.style.transition = '';
            }
          }, 50);
        }
        
        // 重置标记
        isInitialLoad.current = false;
        skipAnimation.current = false;
        window.skipMainContainerAnimation = false;
      }
    }
  }, [activeIndex, navPages, onCategoryChange, pageHeight, dispatch]);

  // 监听导航项更新事件，优化处理逻辑
  useEffect(() => {
    const handleNavItemsUpdated = (event) => {
      if (loading) return;
      
      // 创建新页面
      const newPages = navItems.map(item => ({
        id: item.id,
        route: item.id === 'home' ? '/' : `/category/${item.id}`,
        component: <Home key={item.id} category={item.id === 'home' ? undefined : item.id} />
      }));
      
      // 获取事件中的详细信息
      const { detail } = event;
      const isNewNav = detail?.isNewNav;
      const newNavId = detail?.newNavId;
      const isFromSync = detail?.isFromSync;  // 检查是否是从同步操作回来的
      const isReset = detail?.isReset;  // 检查是否是重置操作
      const isReorder = detail?.isReorder;  // 检查是否是重排序操作
      
      if (isFromSync) {
        // 如果是从服务器同步回来的数据，完全更新页面数组
        setNavPages(newPages);
        
        // 保持当前选中的页面，如果当前选中的页面不在新的页面列表中，则回到首页
        const currentPageId = navPages[activeIndex]?.id;
        const newIndex = newPages.findIndex(page => page.id === currentPageId);
        
        if (newIndex !== -1) {
          // 如果当前页面在新的页面列表中，保持选中
          if (newIndex !== activeIndex) {
            setActiveIndex(newIndex);
          }
        } else {
          // 如果当前页面不在新的页面列表中，回到首页
          setActiveIndex(0);
        }
      } else if (isReset) {
        // 如果是重置操作
        setNavPages(newPages);
        setActiveIndex(0);
      } else if (isNewNav && newNavId) {
        // 如果是新增分类，找到新分类的索引并设置为当前选中项
        const newIndex = newPages.findIndex(page => page.id === newNavId);
        if (newIndex !== -1) {
          setNavPages(newPages);
          setActiveIndex(newIndex);
        }
      } else if (isReorder) {
        // 如果是重排序操作，需要同步更新页面数组
        setNavPages(newPages);
        
        // 保持当前选中的页面ID
        const currentPageId = navPages[activeIndex]?.id;
        const newIndex = newPages.findIndex(page => page.id === currentPageId);
        
        if (newIndex !== -1) {
          setActiveIndex(newIndex);
        } else {
          setActiveIndex(0);
        }
      } else {
        // 其他情况（如编辑等）保持当前页面的激活状态
        setNavPages(prevPages => {
          const currentPage = prevPages[activeIndex];
          if (currentPage) {
            // 仅在页面结构发生变化时更新状态
            if (JSON.stringify(prevPages.map(p => p.id)) !== JSON.stringify(newPages.map(p => p.id))) {
              // 保持当前选中的页面ID
              const newIndex = newPages.findIndex(page => page.id === currentPage.id);
              if (newIndex !== -1 && newIndex !== activeIndex) {
                // 如果页面位置变化了，需要在下一个事件循环中更新activeIndex
                setTimeout(() => setActiveIndex(newIndex), 0);
              }
              return newPages;
            }
          }
          return prevPages;
        });
      }
    };
    
    window.addEventListener('navItemsUpdated', handleNavItemsUpdated);
    return () => {
      window.removeEventListener('navItemsUpdated', handleNavItemsUpdated);
    };
  }, [navItems, activeIndex, loading, navPages]);

  // 添加对 forcePagesRefresh 事件的监听，强制刷新页面
  useEffect(() => {
    const handleForcePagesRefresh = () => {
      // 强制刷新页面数组
      const refreshedPages = navItems.map(item => ({
        id: item.id,
        route: item.id === 'home' ? '/' : `/category/${item.id}`,
        component: <Home key={`refresh_${item.id}_${Date.now()}`} category={item.id === 'home' ? undefined : item.id} />
      }));
      
      // 直接设置新的页面数组
      setNavPages(refreshedPages);
      
      // 回到首页或保持当前页面（如果存在）
      const currentPageId = navPages[activeIndex]?.id;
      const newIndex = refreshedPages.findIndex(page => page.id === currentPageId);
      
      if (newIndex !== -1) {
        // 如果当前页面仍然存在，保持选中
        if (newIndex !== activeIndex) {
          setActiveIndex(newIndex);
        }
      } else {
        // 如果当前页面不存在，回到首页
        setActiveIndex(0);
      }
      
      // 重置内部状态标记
      initialPositionSet.current = false;
      skipAnimation.current = true;
    };
    
    window.addEventListener('forcePagesRefresh', handleForcePagesRefresh);
    return () => {
      window.removeEventListener('forcePagesRefresh', handleForcePagesRefresh);
    };
  }, [navItems, navPages, activeIndex]);

  // 监听分类重置事件，当收到事件时重置为首页
  useEffect(() => {
    const handleNavReset = (event) => {
      // 将activeIndex设置为0（首页）
      setActiveIndex(0);
      
      // 设置位置无动画直接跳转
      if (containerRef.current) {
        containerRef.current.style.transition = 'none';
        containerRef.current.style.transform = 'translateY(0)'; // 回到首页位置
        
        // 恢复过渡效果
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.style.transition = '';
          }
        }, 50);
      }
    };
    
    window.addEventListener('navReset', handleNavReset);
    return () => {
      window.removeEventListener('navReset', handleNavReset);
    };
  }, [navItems, navPages, activeIndex]);

  // 合并导航页面和自定义页面
  const pages = useMemo(() => {
    // 过滤掉无效的页面
    const validPages = [...navPages].filter(page => 
      page && page.id && page.component
    );
    return validPages;
  }, [navPages]);

  // 创建一个循环滚动函数，使最后一页和第一页循环连接
  const circularScrollTo = (targetIndex, duration = 200) => {
    const totalPages = pages.length;
    if (totalPages <= 1) return; // 页面不足，无需循环
    
    // 标记正在进行循环滚动
    isCircularScrolling.current = true;
    
    // 目标索引的特殊处理
    let finalIndex = targetIndex;
    
    // 如果从最后一页向后滚动，去到第一页
    if (targetIndex >= totalPages) {
      finalIndex = 0;
    } 
    // 如果从第一页向前滚动，去到最后一页
    else if (targetIndex < 0) {
      finalIndex = totalPages - 1;
    }
    
    // 设置活跃页面索引
    setActiveIndex(finalIndex);
    
    // 使用平滑滚动到目标位置
    smoothScrollTo(finalIndex, duration);
    
    // 动画结束后重置标记
    setTimeout(() => {
      isCircularScrolling.current = false;
    }, duration);
  };

  useEffect(() => {
    // 如果正在拖拽或动画冻结期，不执行滚动动画
    const isDragging = document.body.hasAttribute('data-dragging');
    const isAnimationFrozen = document.body.classList.contains('animation-freeze');
    
    // 通知父组件当前分类变化
    if (pages.length > 0 && activeIndex >= 0 && activeIndex < pages.length) {
      onCategoryChange && onCategoryChange(pages[activeIndex].id);
      
      // 只有在非拖拽和非冻结状态下才执行滚动动画
      if (!isDragging && !isAnimationFrozen && containerRef.current) {
        // 直接设置位置而不使用动画的情况:
        // 1. window.skipMainContainerAnimation 为 true
        // 2. 初始加载时 (isInitialLoad.current 为 true)
        // 3. 刷新加载且非首页 (skipAnimation.current 为 true)
        // 4. 初始渲染未完成
        if (window.skipMainContainerAnimation || 
            isInitialLoad.current || 
            skipAnimation.current || 
            !initialRenderComplete) {
            
          containerRef.current.style.transition = 'none';
          containerRef.current.style.transform = `translateY(-${activeIndex * pageHeight}px)`;
          
          // 恢复过渡效果
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.style.transition = '';
            }
            window.skipMainContainerAnimation = false;
            if (isInitialLoad.current) {
            isInitialLoad.current = false;
            }
            // 重置跳过动画标记，确保后续导航有动画效果
            skipAnimation.current = false;
          }, 50);
        } else {
          // 正常情况下使用平滑滚动，带有延迟以避免多次触发
          const executeScroll = () => {
            if (containerRef.current) {
              smoothScrollTo(activeIndex);
            }
          };
          // 使用requestAnimationFrame确保在下一帧执行，避免抖动
          requestAnimationFrame(executeScroll);
        }
      }
    } else if (pages.length > 0) {
      // 如果activeIndex超出范围但有可用页面，则重置为首页
      setActiveIndex(0);
    }
  }, [activeIndex, onCategoryChange, pageHeight, pages, initialRenderComplete]);

  // 添加窗口大小变化监听
  useEffect(() => {
    const handleResize = () => {
      const newPageHeight = window.innerHeight;
      
      // 更新 pageHeight 状态
      setPageHeight(newPageHeight);
      
      // 更新容器位置
      if (containerRef.current) {
        const newTransform = `translateY(-${activeIndex * newPageHeight}px)`;
        containerRef.current.style.transform = newTransform;
      }
      
      // 更新容器样式状态
      setContainerStyle(prev => ({
        ...prev,
        height: `${pages.length * 100}vh`,
        transform: `translateY(-${activeIndex * newPageHeight}px)`
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeIndex, pages.length]);

  // 添加一个监听器，确保拖拽过程中主容器位置固定
  useEffect(() => {
    // 用来锁定主容器位置的函数
    const lockMainContainerPosition = () => {
      if (document.body.hasAttribute('data-dragging') && containerRef.current) {
        // 计算当前containerRef的位置
        const currentPage = Math.min(activeIndex, pages.length - 1);
        const pagePosition = -currentPage * pageHeight;
        
        // 禁用过渡动画
        containerRef.current.style.transition = 'none';
        
        // 设置固定位置
        window.requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.style.transform = `translateY(${pagePosition}px)`;
          }
        });
      }
    };
    
    // 创建MutationObserver来监听body的data-dragging属性变化
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-dragging') {
          const isDragging = document.body.hasAttribute('data-dragging');
          if (isDragging) {
            lockMainContainerPosition();
          }
        }
      });
    });
    
    // 开始观察
    observer.observe(document.body, { attributes: true });
    
    // 清理函数
    return () => observer.disconnect();
  }, [activeIndex, pageHeight, pages.length]);

  const handleTouchStart = (e) => {
    // 如果弹窗正在显示，则不处理触摸事件
    if (isPopupVisible()) return;
    
    // **新增**: 检查是否是 @dnd-kit 拖拽操作
    const isDndKitDragging = e.target.closest('[data-dnd-kit-draggable-id]') || 
                            e.target.closest('.app-item') ||
                            e.target.closest('[draggable="true"]') ||
                            document.body.hasAttribute('data-dragging');
    
    // **新增**: 如果是拖拽操作，完全不处理，让 @dnd-kit 接管
    if (isDndKitDragging) {
      return;
    }
    
    // 开始新的触摸交互
    touchInteractionActive.current = true;
    hasTriggeredTouchPageChange.current = false;
    
    // 检查是否在页面切换冷却期内
    if (!canChangePage.current) {
      // 允许内容滚动，但阻止页面切换
      const { isScrollable, element } = isTouchOnScrollableContent(e);
      if (isScrollable) {
        // 仅设置可滚动状态，但不跟踪页面切换相关的状态
        touchTargetRef.current = element;
        isScrollingContent.current = isScrollable && isVerticalScrollable(element);
      } else {
        // 非滚动内容全部阻止
        e.preventDefault();
      }
      return;
    }
    
    // 检查触摸是否在可滚动内容上
    const { isScrollable, element } = isTouchOnScrollableContent(e);
    touchTargetRef.current = element;
    isScrollingContent.current = isScrollable && isVerticalScrollable(element);
    
    // 如果是在可滚动内容上，记录初始位置
    if (isScrollingContent.current && element) {
      lastScrollTop.current = element.scrollTop;
      scrollDirection.current = null;
      
      // 标记用户开始新的触摸交互，重置停止状态
      if (element.hasAttribute('data-touch-boundary')) {
        element.setAttribute('data-touch-stopped', 'false');
      }
    }
    
    initialTouchY.current = e.touches[0].clientY;
    touchStartY.current = e.touches[0].clientY;
    lastTouchY.current = e.touches[0].clientY;
    lastTouchTime.current = performance.now();
    velocityY.current = 0;
    touchMoveY.current = 0;
    
    if (containerRef.current) {
      containerRef.current.style.transition = 'none';
    }
    
    // 仅在非可滚动内容上阻止默认行为
    if (!isScrollingContent.current) {
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    // 如果弹窗正在显示，则不处理触摸移动事件
    if (isPopupVisible()) return;
    
    // 检查是否在页面切换冷却期内
    if (!canChangePage.current) {
      // 允许内容滚动，但阻止页面切换相关的处理
      if (!isScrollingContent.current) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - lastTouchY.current;
    
    // 处理在可滚动内容上的触摸移动
    if (isScrollingContent.current && touchTargetRef.current) {
      const element = touchTargetRef.current;
      
      // 如果没有设置滚动方向，则确定滚动方向
      if (scrollDirection.current === null && Math.abs(deltaY) > 5) {
        scrollDirection.current = deltaY > 0 ? 'down' : 'up';
      }
      
      // 检查当前滚动位置
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      const canScrollDown = scrollTop < (scrollHeight - clientHeight - 5);
      const canScrollUp = scrollTop > 5;
      
      // 向下拖拽（内容向上滚动）
      if (deltaY > 0) {
        if (canScrollDown) {
          // 内容还能继续向下滚动，重置边界状态
          element.removeAttribute('data-touch-boundary');
          element.removeAttribute('data-touch-direction');
          // 让内容自然滚动
          lastTouchY.current = currentY;
          lastTouchTime.current = performance.now();
          return;
        } else {
          // 内容已经不能向下滚动，到达底部
        e.preventDefault();
        e.stopPropagation();
          
          const isAtBoundary = element.hasAttribute('data-touch-boundary');
          const boundaryDirection = element.getAttribute('data-touch-direction');
          
          if (!isAtBoundary || boundaryDirection !== 'down') {
            // 第一次到达底部边界
            element.setAttribute('data-touch-boundary', 'true');
            element.setAttribute('data-touch-direction', 'down');
            element.setAttribute('data-touch-stopped', 'false');
            return;
      } else {
            // 检查用户是否已经停止过触摸
            const hasStopped = element.getAttribute('data-touch-stopped') === 'true';
            if (!hasStopped) {
              return;
            }
            
            // 检查停止后重新开始触摸的时间间隔
            const stopTime = parseInt(element.getAttribute('data-touch-stop-time') || '0');
            const now = Date.now();
            if (now - stopTime < 200) { // 200ms最小间隔
              return;
            }
            
            // 已经在底部边界且用户停止后重新开始滑动，允许页面切换
            element.removeAttribute('data-touch-boundary');
            element.removeAttribute('data-touch-direction');
            element.removeAttribute('data-touch-stopped');
            element.removeAttribute('data-touch-stop-time');
            
            // 检查是否已经触发过页面切换
            if (hasTriggeredTouchPageChange.current) {
              return;
            }
            hasTriggeredTouchPageChange.current = true;
            
            // 继续执行页面切换逻辑
          }
        }
      }
      
      // 向上拖拽（内容向下滚动）
      if (deltaY < 0) {
        if (canScrollUp) {
          // 内容还能继续向上滚动，重置边界状态
          element.removeAttribute('data-touch-boundary');
          element.removeAttribute('data-touch-direction');
          // 让内容自然滚动
        lastTouchY.current = currentY;
        lastTouchTime.current = performance.now();
          return;
        } else {
          // 内容已经不能向上滚动，到达顶部
          e.preventDefault();
          e.stopPropagation();
          
          const isAtBoundary = element.hasAttribute('data-touch-boundary');
          const boundaryDirection = element.getAttribute('data-touch-direction');
          
          if (!isAtBoundary || boundaryDirection !== 'up') {
            // 第一次到达顶部边界
            element.setAttribute('data-touch-boundary', 'true');
            element.setAttribute('data-touch-direction', 'up');
            element.setAttribute('data-touch-stopped', 'false');
            return;
          } else {
            // 检查用户是否已经停止过触摸
            const hasStopped = element.getAttribute('data-touch-stopped') === 'true';
            if (!hasStopped) {
        return;
            }
            
            // 检查停止后重新开始触摸的时间间隔
            const stopTime = parseInt(element.getAttribute('data-touch-stop-time') || '0');
            const now = Date.now();
            if (now - stopTime < 200) { // 200ms最小间隔
              return;
            }
            
            // 已经在顶部边界且用户停止后重新开始滑动，允许页面切换
            element.removeAttribute('data-touch-boundary');
            element.removeAttribute('data-touch-direction');
            element.removeAttribute('data-touch-stopped');
            element.removeAttribute('data-touch-stop-time');
            
            // 检查是否已经触发过页面切换
            if (hasTriggeredTouchPageChange.current) {
              return;
            }
            hasTriggeredTouchPageChange.current = true;
            
            // 继续执行页面切换逻辑
          }
        }
      }
    } else {
      // 非可滚动内容阻止默认行为
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (scrolling.current) return;
    
    const currentTime = performance.now();
    const deltaTime = currentTime - lastTouchTime.current;
    
    // 计算瞬时速度，使用合理系数，避免过度灵敏
    if (deltaTime > 0) {
      velocityY.current = deltaY / deltaTime;
    }
    
    touchEndY.current = currentY;
    const diff = touchStartY.current - touchEndY.current;
    touchMoveY.current = diff;
    
    // 实时跟随手指移动，使用合适的阻尼效果
    if (containerRef.current) {
      const maxScroll = (pages.length - 1) * pageHeight;
      let moveY = activeIndex * pageHeight + diff;
      
      // 调整阻尼效果，确保边界滚动更可控
      if (moveY < -pageHeight * 0.3) { 
        // 首页往上拉，预览最后一页
        const overScroll = -moveY;
        moveY = -(overScroll / (Math.abs(overScroll) * 0.05 + 1.5)); // 增加阻尼，提高控制性
      } else if (moveY > maxScroll + pageHeight * 0.3) {
        // 末页往下拉，预览首页
        const overScroll = moveY - maxScroll;
        moveY = maxScroll + (overScroll / (Math.abs(overScroll) * 0.05 + 1.5)); // 增加阻尼，提高控制性
      }
      
      containerRef.current.style.transform = `translateY(-${moveY}px)`;
    }
    
    lastTouchY.current = currentY;
    lastTouchTime.current = currentTime;
  };

  const handleTouchEnd = (e) => {
    // 如果弹窗正在显示或在冷却期，则不处理触摸结束事件
    if (isPopupVisible() || !canChangePage.current) {
      // 重置触摸交互状态
      touchInteractionActive.current = false;
      hasTriggeredTouchPageChange.current = false;
      return;
    }
    
    // 如果是在可滚动内容上进行滚动，标记用户停止了触摸
    if (isScrollingContent.current && touchTargetRef.current) {
      const element = touchTargetRef.current;
      
      // 如果在边界状态，标记用户已停止触摸
      if (element.hasAttribute('data-touch-boundary')) {
        element.setAttribute('data-touch-stopped', 'true');
        element.setAttribute('data-touch-stop-time', Date.now().toString());
      }
      
      const currentScrollTop = element.scrollTop;
      
      // 检查是否真的发生了内容滚动
      const hasScrolled = Math.abs(currentScrollTop - lastScrollTop.current) > 5;
      
      // 如果内容已滚动，不触发页面切换
      if (hasScrolled) {
        // 重置状态
        isScrollingContent.current = false;
        touchTargetRef.current = null;
        scrollDirection.current = null;
        touchInteractionActive.current = false;
        hasTriggeredTouchPageChange.current = false;
        return;
      }
      
      // 检查是否在边界
      const isAtTop = currentScrollTop <= 0;
      const isAtBottom = currentScrollTop + element.clientHeight >= element.scrollHeight - 1;
      
      // 如果不在临界位置，也不触发页面切换
      if (!isAtTop && !isAtBottom) {
        // 重置状态
        isScrollingContent.current = false;
        touchTargetRef.current = null;
        scrollDirection.current = null;
        touchInteractionActive.current = false;
        hasTriggeredTouchPageChange.current = false;
        return;
      }
    }
    
    // 仅在非可滚动内容上阻止默认行为
    if (!isScrollingContent.current) {
      e.preventDefault();
    }
    
    if (scrolling.current) {
      // 重置触摸交互状态
      touchInteractionActive.current = false;
      hasTriggeredTouchPageChange.current = false;
      return;
    }

    const diff = touchMoveY.current;
    const velocity = velocityY.current;
    const threshold = 40;
    
    const shouldChangePage = Math.abs(diff) > threshold || Math.abs(velocity) > 0.5;
    
    if (shouldChangePage) {
      // 检查是否已经触发过页面切换
      if (hasTriggeredTouchPageChange.current) {
        // 重置状态
        isScrollingContent.current = false;
        touchTargetRef.current = null;
        scrollDirection.current = null;
        touchInteractionActive.current = false;
        hasTriggeredTouchPageChange.current = false;
        return;
      }
      
      hasTriggeredTouchPageChange.current = true;
      
      let newIndex = activeIndex;
      
      if ((diff > 0 || velocity < -0.5)) {
        newIndex = activeIndex + 1;
        
        if (newIndex >= pages.length) {
          circularScrollTo(newIndex, 300);
          // 重置状态
          isScrollingContent.current = false;
          touchTargetRef.current = null;
          scrollDirection.current = null;
          touchInteractionActive.current = false;
          hasTriggeredTouchPageChange.current = false;
          return;
        }
      } else if ((diff < 0 || velocity > 0.5)) {
        newIndex = activeIndex - 1;
        
        if (newIndex < 0) {
          circularScrollTo(newIndex, 300);
          // 重置状态
          isScrollingContent.current = false;
          touchTargetRef.current = null;
          scrollDirection.current = null;
          touchInteractionActive.current = false;
          hasTriggeredTouchPageChange.current = false;
          return;
        }
      }

      newIndex = Math.max(0, Math.min(newIndex, pages.length - 1));
      smoothScrollTo(newIndex, 300);
    } else {
      smoothScrollTo(activeIndex, 250);
    }
    
    // 重置状态
    isScrollingContent.current = false;
    touchTargetRef.current = null;
    scrollDirection.current = null;
    touchInteractionActive.current = false;
    hasTriggeredTouchPageChange.current = false;
  };

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    goToPage: (index) => {
 
      if (index >= 0 && index < pages.length) {
        setActiveIndex(index);
      }
    },
    goToCustomPage: (route, title) => {

      
      // 检查是否已存在相同路由的页面
      const existingPageIndex = navPages.findIndex(page => page.route === route);
      
      if (existingPageIndex !== -1) {
        // 如果页面已存在，直接跳转
        setActiveIndex(existingPageIndex);
      } else {
        // 如果页面不存在，创建新页面并跳转
        const navId = route.slice(1).replace('category/', '');
        const pageId = navId;
        
        
        const newPage = {
          id: pageId,
          route,
          title,
          component: <Home key={pageId} category={navId} />
        };
        
        // 更新navPages
        setNavPages(prev => {
          const newPages = [...prev, newPage];
          
        
          
          // 触发自定义事件，标记这是新增分类
          const event = new CustomEvent('navItemsUpdated', {
            detail: {
              isNewNav: true,
              newNavId: pageId
            }
          });
          window.dispatchEvent(event);
          
          // 设置新页面为活动页面
          setTimeout(() => {
            setActiveIndex(newPages.length - 1);
          }, 0);
          
          return newPages;
        });
      }
    }
  }));

  // 添加事件监听器
  useEffect(() => {
    let isAtBoundary = false; // 是否在边界
    let boundaryDirection = null; // 边界方向：'down' 或 'up'
    let boundaryTime = 0; // 到达边界的时间
    let hasUserPaused = false; // 用户是否已经停顿过
    let lastWheelTime = 0; // 最后一次滚轮事件时间
    let wheelInteractionActive = false; // 滚轮交互是否活跃
    let hasTriggeredPageChange = false; // 本次交互是否已经触发过页面切换
    const PAUSE_DETECTION_DELAY = 200; // 停顿检测延迟，毫秒
    const WHEEL_INTERACTION_TIMEOUT = 150; // 滚轮交互超时时间，毫秒
    
    // 停顿检测定时器
    let pauseTimer = null;
    let wheelInteractionTimer = null;
    
    const handleWheel = (e) => {
      // 如果弹窗正在显示或在冷却期，则不处理滚轮事件
      if (isPopupVisible() || !canChangePage.current) return;
      
      // **重要**: 检查是否正在拖拽，如果是则不处理滚轮事件
      if (document.body.hasAttribute('data-dragging') || 
          window._isAppDragging || 
          window._dndKitDragging) {
        return;
      }
      
      const now = Date.now();
      if (scrolling.current || now - lastScrollTime.current < 50) return;
      
      // 开始或继续滚轮交互
      if (!wheelInteractionActive) {
        wheelInteractionActive = true;
        hasTriggeredPageChange = false;
      }
      
      // 重置交互超时定时器
      if (wheelInteractionTimer) {
        clearTimeout(wheelInteractionTimer);
      }
      wheelInteractionTimer = setTimeout(() => {
        wheelInteractionActive = false;
        hasTriggeredPageChange = false;
      }, WHEEL_INTERACTION_TIMEOUT);
      
      // 如果本次交互已经触发过页面切换，忽略后续滚轮事件
      if (hasTriggeredPageChange) return;
      
      // 更新最后滚轮时间
      lastWheelTime = now;
      
      // 清除之前的停顿检测定时器
      if (pauseTimer) {
        clearTimeout(pauseTimer);
      }
      
      // 如果在边界状态，设置停顿检测定时器
      if (isAtBoundary) {
        pauseTimer = setTimeout(() => {
          hasUserPaused = true;
        }, PAUSE_DETECTION_DELAY);
      }
      
      // 检查当前页面是否有可滚动的内容
      const currentPage = containerRef.current?.children[activeIndex];
      if (currentPage) {
        // 查找Home页面中的apps-wrapper元素（这个元素是实际可滚动的）
        const appsWrapper = currentPage.querySelector('.apps-wrapper');
        
        if (appsWrapper) {
          // 检查内容是否可以滚动
          const scrollTop = appsWrapper.scrollTop;
          const scrollHeight = appsWrapper.scrollHeight;
          const clientHeight = appsWrapper.clientHeight;
          const canScrollDown = scrollTop < (scrollHeight - clientHeight - 5);
          const canScrollUp = scrollTop > 5;
          const isScrollable = scrollHeight > clientHeight;
          
          // 如果内容可以滚动，优先处理内容滚动
          if (isScrollable) {
            // 向下滚动
            if (e.deltaY > 0) {
              if (canScrollDown) {
                // 内容还能继续滚动，重置所有边界状态，让内容自然滚动
                isAtBoundary = false;
                boundaryDirection = null;
                boundaryTime = 0;
                hasUserPaused = false;
                return; // 不阻止默认行为，让内容滚动
              } else {
                // 到达底部边界
                e.preventDefault(); // 阻止默认滚动
                
                if (!isAtBoundary || boundaryDirection !== 'down') {
                  // 第一次到达底部边界，标记状态但不切换页面
                  isAtBoundary = true;
                  boundaryDirection = 'down';
                  boundaryTime = now;
                  hasUserPaused = false; // 重置停顿状态
                  return;
                } else {
                  // 检查用户是否已经停顿过
                  if (!hasUserPaused) {
                    return;
                  }
                  
                  // 用户已经停顿过并再次滑动，执行页面切换
                  isAtBoundary = false;
                  boundaryDirection = null;
                  boundaryTime = 0;
                  hasUserPaused = false;
                  hasTriggeredPageChange = true; // 标记已触发页面切换
                  
                  // 执行页面切换
                  scrolling.current = true;
                  let newIndex = activeIndex + 1;
                  if (newIndex >= pages.length) {
                    circularScrollTo(newIndex, 200);
                  } else {
                    smoothScrollTo(newIndex, 200);
                  }
                  return;
                }
              }
            }
            
            // 向上滚动
            if (e.deltaY < 0) {
              if (canScrollUp) {
                // 内容还能继续滚动，重置所有边界状态，让内容自然滚动
                isAtBoundary = false;
                boundaryDirection = null;
                boundaryTime = 0;
                hasUserPaused = false;
                return; // 不阻止默认行为，让内容滚动
              } else {
                // 到达顶部边界
                e.preventDefault(); // 阻止默认滚动
                
                if (!isAtBoundary || boundaryDirection !== 'up') {
                  // 第一次到达顶部边界，标记状态但不切换页面
                  isAtBoundary = true;
                  boundaryDirection = 'up';
                  boundaryTime = now;
                  hasUserPaused = false; // 重置停顿状态
                  return;
                } else {
                  // 检查用户是否已经停顿过
                  if (!hasUserPaused) {
                    return;
                  }
                  
                  // 用户已经停顿过并再次滑动，执行页面切换
                  isAtBoundary = false;
                  boundaryDirection = null;
                  boundaryTime = 0;
                  hasUserPaused = false;
                  hasTriggeredPageChange = true; // 标记已触发页面切换
                  
                  // 执行页面切换
                  scrolling.current = true;
                  let newIndex = activeIndex - 1;
                  if (newIndex < 0) {
                    circularScrollTo(newIndex, 200);
                  } else {
                    smoothScrollTo(newIndex, 200);
                  }
                  return;
                }
              }
            }
          }
        }
      }
      
      // 如果没有可滚动内容，直接执行页面切换
      e.preventDefault();
      lastScrollTime.current = now;
      
      if (Math.abs(e.deltaY) > 10) {
        hasTriggeredPageChange = true; // 标记已触发页面切换
        
        scrolling.current = true;
        let newIndex = activeIndex;
        
        if (e.deltaY > 0) {
          newIndex = activeIndex + 1;
          
          // 如果是最后一页，则循环到首页
          if (newIndex >= pages.length) {
            circularScrollTo(newIndex, 200);
            return;
          }
        } else if (e.deltaY < 0) {
          newIndex = activeIndex - 1;
          
          // 如果是首页，则循环到最后一页
          if (newIndex < 0) {
            circularScrollTo(newIndex, 200);
            return;
          }
        }

        // 确保新索引在有效范围内
        newIndex = Math.max(0, Math.min(newIndex, pages.length - 1));
        smoothScrollTo(newIndex, 200);
        
        // 额外的保险措施，确保滚动状态最终会被重置
        setTimeout(() => {
          scrolling.current = false;
        }, 500);
      }
    };

    // 添加非passive的wheel事件监听器
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      // 清理定时器
      if (pauseTimer) {
        clearTimeout(pauseTimer);
      }
      if (wheelInteractionTimer) {
        clearTimeout(wheelInteractionTimer);
      }
    };
  }, [activeIndex, pages.length, pageHeight]);

  return (
    <div 
      className="main-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {pages.length > 0 ? (
        <>
          <div 
            ref={containerRef} 
            className="pages-container"
            style={containerStyle}
          >
            {pages.map((page, index) => (
              <div 
                key={page.id}
                className={`page ${activeIndex === index ? 'active' : ''}`}
                style={{ 
                  top: `${index * pageHeight}px`, 
                  height: `${pageHeight}px`,
                  willChange: 'transform'
                }}
              >
                {page.component}
              </div>
            ))}
          </div>
          
          <div className="pagination-dots">
            {pages.map((page, index) => (
              <div 
                key={page.id}
                className={`dot ${activeIndex === index ? 'active' : ''}`}
                onClick={() => {
                  if (scrolling.current) return;
                  smoothScrollTo(index, 300);
                }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="loading-container">
          <div className="loading-text">加载中...</div>
        </div>
      )}
    </div>
  );
});

export default MainContainer;